// /pricing — three tiers. Honest: hosted is free during beta, enterprise
// is 'contact us'. No pricing surface for anything we can't deliver.
import { Link } from "react-router-dom";
import { buttonClass } from "@/components/ui";
import { useSeo } from "@/hooks";
import { MarketingShell } from "./MarketingShell";

type Tier = {
  name: string;
  price: React.ReactNode;
  cadence?: string;
  tagline: string;
  features: string[];
  cta: { label: string; to?: string; href?: string; variant: "cta" | "primary" | "ghost" };
  featured?: boolean;
};

const TIERS: Tier[] = [
  {
    name: "Self-hosted",
    price: "Coming soon",
    tagline: "Deploy the whole stack on your own machines, with your own storage.",
    features: [
      "Nix flake deploys the whole stack",
      "Bring-your-own-S3 (MinIO, Backblaze, R2, AWS)",
      "No telemetry, no phone-home",
      "Private deployments available on request",
      "Early-access list open — email for details",
    ],
    cta: { label: "Contact for access", href: "mailto:neel@neels.dev?subject=Self-hosted%20malbox", variant: "primary" },
  },
  {
    name: "Hosted",
    price: "Free",
    cadence: "during beta",
    tagline: "Submit a sample from the browser. We run the hardware.",
    features: [
      "Everything on malbox.neels.dev",
      "Shared fleet, two concurrent VMs",
      "Up to 5 bulk submissions per day signed-in",
      "60-day artifact retention",
      "Submissions remain private to your account",
    ],
    cta: { label: "Try it →", to: "/submit", variant: "cta" },
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Talk to us",
    tagline: "Dedicated hardware, SLA, longer retention, custom integrations.",
    features: [
      "Dedicated analysis fleet",
      "99.9% uptime SLA",
      "Custom guest profiles (locale, software, extensions)",
      "SIEM / SOAR integration (Splunk, Elastic, Chronicle)",
      "Private deployment / air-gap option",
      "Priority support",
    ],
    cta: { label: "Contact sales", href: "mailto:neel@neels.dev?subject=Enterprise%20inquiry", variant: "primary" },
  },
];

function TierCard({ t }: { t: Tier }) {
  const border = t.featured ? "border-ink" : "border-rule-row";
  const bg = t.featured ? "bg-raised" : "bg-paper";
  return (
    <div className={`flex min-w-0 flex-col border ${border} ${bg} p-6`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>{t.name}</h2>
        {t.featured && <span className="meta font-mono text-ink-soft">— current</span>}
      </div>
      <div className="mt-5 flex items-baseline gap-2">
        <div className="font-mono text-ink" style={{ fontSize: "clamp(32px, 3vw, 44px)", lineHeight: 1, letterSpacing: "-0.02em" }}>{t.price}</div>
        {t.cadence && <div className="meta text-ink-soft">{t.cadence}</div>}
      </div>
      <p className="mt-3 text-ui text-ink-soft">{t.tagline}</p>
      <ul className="mt-5 flex-1 space-y-2 text-ui text-ink">
        {t.features.map((f) => (
          <li key={f} className="flex gap-2">
            <span className="mt-[0.4em] inline-block h-1 w-1 shrink-0 rounded-full bg-ink" aria-hidden="true" />
            <span className="min-w-0">{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {t.cta.to ? (
          <Link to={t.cta.to} viewTransition className={buttonClass({ variant: t.cta.variant, size: "md", className: "w-full justify-center" })}>
            {t.cta.label}
          </Link>
        ) : (
          <a href={t.cta.href} target={t.cta.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={buttonClass({ variant: t.cta.variant, size: "md", className: "w-full justify-center" })}>
            {t.cta.label}
          </a>
        )}
      </div>
    </div>
  );
}

export function PricingPage() {
  useSeo({
    title: "Pricing — malbox malware sandbox",
    description: "Honest pricing. Hosted malbox is free during beta. Self-hosted deployments and enterprise plans with dedicated hardware, SLA, SIEM integration — contact for early access.",
    path: "/pricing",
    keywords: "malware sandbox pricing, free malware sandbox, enterprise malware sandbox, self-hosted malware analysis, malware analysis pricing, dedicated malware sandbox",
  });
  return (
    <MarketingShell
      eyebrow="Pricing"
      title="Honest pricing. Open source."
      lede={<>Self-host the whole thing for free. Use the hosted beta for free. If you need dedicated hardware or SLAs, let's talk.</>}
    >
      <div className="grid gap-4 lg:grid-cols-3">
        {TIERS.map((t) => <TierCard key={t.name} t={t} />)}
      </div>

      <section className="mt-14 max-w-[720px] text-ui text-ink-soft">
        <h2 className="font-mono text-ink" style={{ fontSize: "20px", letterSpacing: "-0.015em" }}>Why there isn't a credit card form</h2>
        <p className="mt-3">
          Hosted is on us while we're in beta — the fleet runs on fixed hardware, the storage bill is small, and we'd rather have real users submitting real samples than ten paid plans and no feedback. When pricing opens we'll grandfather existing accounts.
        </p>
      </section>
    </MarketingShell>
  );
}
